import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/common/Button";
import {
  ErrorMessage,
  FormContainer,
  FormGroup,
  FormHeading,
  Input,
  Label,
} from "../components/common/Form";
import { useProfileOwner, useUserStore } from "../stores/user.store";
import {
  PageTitle,
  PageTitleDetail,
  PageTitleSubtitle,
} from "../components/common/PageData";
import { supabaseClient } from "../shared/supabase";
import { format } from "date-fns";
import { BackButton } from "../components/common/BackButton";
import { DateValidation } from "../utils/dateValidation";

type FormInputs = {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  province: string;
  phone: string;
  birthday: string;
};

export const EditAccount = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const { profile, loadProfile } = useUserStore();
  const profileOwner = useProfileOwner();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>();

  useEffect(() => {
    if (!profileOwner) return;

    reset({
      firstName: profileOwner?.first_name || "",
      lastName: profileOwner?.last_name || "",
      address: profile?.address || "",
      city: profile?.city || "",
      province: profile?.province || "",
      phone: profile?.phone || "",
      birthday: profileOwner?.date_of_birth || "",
    });
  }, [profileOwner, profile, reset]);

  const formatRegistrationDate = (dateString?: string) => {
    if (!dateString) return "";
    return format(new Date(dateString), "dd/MM/yyyy");
  };

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (!profile || !profileOwner) return;

    setIsLoading(true);
    setUpdateSuccess(false);

    try {
      // Update profile data
      const profileUpdate = await supabaseClient
        .from("profiles")
        .update({
          address: data.address,
          city: data.city,
          province: data.province,
          phone: data.phone,
        })
        .eq("id", profile.id);

      if (profileUpdate.error) throw profileUpdate.error;

      // Update member (owner) data
      const memberUpdate = await supabaseClient
        .from("members")
        .update({
          first_name: data.firstName,
          last_name: data.lastName,
          date_of_birth: data.birthday,
        })
        .eq("id", profileOwner.id);

      if (memberUpdate.error) throw memberUpdate.error;

      // Reload profile data
      await loadProfile();
      setUpdateSuccess(true);

      // Navigate back to account page after 2 seconds
      setTimeout(() => {
        navigate("/account");
      }, 2000);
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormContainer>
      <BackButton onClick={() => navigate("/account")} />

      <PageTitle>DATI PERSONALI</PageTitle>
      {profileOwner && (
        <>
          <PageTitleSubtitle>
            {profileOwner.first_name} {profileOwner.last_name}
          </PageTitleSubtitle>
          <PageTitleDetail>{profile?.email}</PageTitleDetail>
          <PageTitleDetail>
            ISCRITTO IL {formatRegistrationDate(profileOwner.created_at)}
          </PageTitleDetail>
        </>
      )}

      <FormHeading>
        IN QUESTA SEZIONE PUOI MODIFICARE I TUOI DATI PERSONALI INSERITI IN FASE
        DI REGISTRAZIONE
      </FormHeading>

      {updateSuccess && (
        <div
          style={{
            padding: "10px",
            backgroundColor: "#d4edda",
            color: "#155724",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          Dati aggiornati con successo! Verrai reindirizzato alla pagina del tuo
          account.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="firstName">Nome</Label>
          <Input
            id="firstName"
            $hasError={!!errors.firstName}
            {...register("firstName", { required: "Campo obbligatorio" })}
          />
          {errors.firstName && (
            <ErrorMessage role="alert">{errors.firstName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lastName">Cognome</Label>
          <Input
            id="lastName"
            $hasError={!!errors.lastName}
            {...register("lastName", { required: "Campo obbligatorio" })}
          />
          {errors.lastName && (
            <ErrorMessage role="alert">{errors.lastName.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="address">Indirizzo</Label>
          <Input
            id="address"
            $hasError={!!errors.address}
            {...register("address")}
          />
          {errors.address && (
            <ErrorMessage role="alert">{errors.address.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="city">Città</Label>
          <Input
            id="city"
            $hasError={!!errors.city}
            {...register("city", { required: "Campo obbligatorio" })}
          />
          {errors.city && (
            <ErrorMessage role="alert">{errors.city.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="province">Provincia</Label>
          <Input
            id="province"
            $hasError={!!errors.province}
            {...register("province")}
          />
          {errors.province && (
            <ErrorMessage role="alert">{errors.province.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="phone">Cellulare</Label>
          <Input
            id="phone"
            type="tel"
            $hasError={!!errors.phone}
            {...register("phone", {
              required: "Campo obbligatorio",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Inserisci un numero di telefono valido",
              },
            })}
          />
          {errors.phone && (
            <ErrorMessage role="alert">{errors.phone.message}</ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="birthday">Data di nascita</Label>
          <Input
            id="birthday"
            type="date"
            min={DateValidation.getMinDateString()}
            max={DateValidation.getTodayString()}
            $hasError={!!errors.birthday}
            {...register("birthday", DateValidation.getAdultValidationRules())}
          />
          {errors.birthday && (
            <ErrorMessage role="alert">{errors.birthday.message}</ErrorMessage>
          )}
        </FormGroup>

        <Button fullWidth type="submit" disabled={isLoading}>
          {isLoading ? "SALVATAGGIO IN CORSO..." : "SALVA MODIFICHE"}
        </Button>
      </form>
    </FormContainer>
  );
};
